// 文件编码: UTF-8
// 全局请求错误处理：统一中文提示、移除未使用导入、保留降级方案
import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { notification } from 'antd';

// 全局 message 实例
let messageInstance: any = null;

// 设置全局 message 实例
export const setMessageInstance = (instance: any) => {
	messageInstance = instance;
};

// 获取 message 实例的辅助函数
export const getMessage = () => {
	if (messageInstance) {
		return messageInstance;
	}
	// 如果没有实例，使用静态方法作为降级方案
	return {
		warning: console.warn,
		error: console.error,
		success: console.log
	};
};

const getCookie = (name: string) => {
	if (typeof document === 'undefined') {
		return undefined;
	}
	const match = document.cookie.match(
		new RegExp(`(?:^|; )${name.replace(/[$()*+./?[\\\]^{|}-]/g, '\\$&')}=([^;]*)`)
	);
	return match ? decodeURIComponent(match[1]) : undefined;
};

// 错误处理方案： 错误类型
enum ErrorShowType {
	SILENT = 0,
	WARN_MESSAGE = 1,
	ERROR_MESSAGE = 2,
	NOTIFICATION = 3,
	REDIRECT = 9
}
// 与后端约定的响应数据格式
interface ResponseStructure {
	success: boolean;
	data: any;
	errorCode?: number;
	errorMessage?: string;
	showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
	// 错误处理： umi@3 的错误处理方案。
	errorConfig: {
		// 错误抛出
		errorThrower: (res) => {
			const { success, data, errorCode, errorMessage, showType } =
				res as unknown as ResponseStructure;
			if (!success) {
				const error: any = new Error(errorMessage);
				error.name = 'BizError';
				error.info = { errorCode, errorMessage, showType, data };
				throw error; // 抛出自制的错误
			}
		},
		// 错误接收及处理
		errorHandler: (error: any, opts: any) => {
			if (opts?.skipErrorHandler) throw error;
			// 我们的 errorThrower 抛出的错误。
			if (error.name === 'BizError') {
				const errorInfo: ResponseStructure | undefined = error.info;
				if (errorInfo) {
					const { errorMessage, errorCode } = errorInfo;
					const message = getMessage();
					switch (errorInfo.showType) {
						case ErrorShowType.SILENT:
							// do nothing
							break;
						case ErrorShowType.WARN_MESSAGE:
							message.warning(errorMessage || '操作警告，请稍后重试');
							break;
						case ErrorShowType.ERROR_MESSAGE:
							message.error(errorMessage || '操作失败，请稍后重试');
							break;
						case ErrorShowType.NOTIFICATION:
							notification.open({
								description: errorMessage || '操作失败，请稍后重试',
								message: errorCode ? `错误码：${errorCode}` : '系统通知'
							});
							break;
						case ErrorShowType.REDIRECT:
							// TODO: redirect
							break;
						default:
							message.error(errorMessage || '操作失败，请稍后重试');
					}
				}
			} else if (error.response) {
				// Axios 的错误
				// 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
				// 忽略 401 错误，由业务层处理重定向
				if (error.response.status !== 401) {
					getMessage().error(`请求错误，状态码：${error.response.status}`);
				}
			} else if (error.request) {
				// 请求已经成功发起，但没有收到响应
				// \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
				// 而在node.js中是 http.ClientRequest 的实例
				getMessage().error('服务器无响应，请稍后重试');
			} else {
				// 发送请求时出了点问题
				getMessage().error('请求发生错误，请稍后重试');
			}
		}
	},

	// 请求拦截器
	requestInterceptors: [
		(config: RequestOptions) => {
			// 拦截请求配置，进行个性化处理。
			// 启用 credentials 以发送 cookies
			config.credentials = 'include';

			// 统一超时与安全请求头
			config.timeout = config.timeout || 15000;
			config.headers = {
				'X-Requested-With': 'XMLHttpRequest',
				'X-App-Version': 'admin-6',
				...config.headers
			};
			const xsrfToken = getCookie('XSRF-TOKEN');
			if (xsrfToken && !config.headers?.['X-XSRF-TOKEN']) {
				config.headers = {
					...config.headers,
					'X-XSRF-TOKEN': xsrfToken
				};
			}
			return config;
		}
	],

	// 响应拦截器
	responseInterceptors: [
		(response) => {
			return response;
		}
	]
};
